import { useCallback, useEffect, useRef } from "react";
import { useAnimationFrame } from "motion/react";

const MIN_FREQUENCY = 0.1;
const MAX_FREQUENCY = 1;

export default function Wave(props) {
  const {
    className = '',
    style = {},
    stroke = `rgb(0 0 0 / 1)`,
    strokeWidth = 3,
    // Wave function parameters
    amplitude = 25,           // A: maximum displacement from equilibrium
    frequency = 1,          // f: number of complete cycles (in terms of 2π)
    wavelength = 1,           // λ: spatial period of the wave
    phaseShift = 0,           // φ: horizontal shift (in radians)
    verticalOffset = 50,      // vertical center position
    angularVelocity = 0.005,   // ω: speed of wave propagation (rad/s)
    segments = 50,            // resolution of the wave rendering
    // inertia = 0.05, 
  } = props || {};
  const pathRef = useRef(null);
  const timeRef = useRef(0);
  const frequencyRef = useRef(frequency);

  // ✄ ------------------------------------------------------------------------
  // Scroll-based frequency modulation (disabled for now)
  // const targetFrequencyRef = useRef(0.1);
  // const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  // const frequencyMomentum = useRef(0);
  // const moveOnScroll = useCallback(() => {
  //   const FREQ_FACTOR = 0.001;      // How much velocity impacts frequency
  //   const DECAY = 0.005;           // Linear decay per frame

  //   // Calculate scroll velocity
  //   const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
  //   const velocity = Math.abs(currentScrollY - lastScrollY.current);
  //   lastScrollY.current = currentScrollY;

  //   // When scrolling, boost frequency and speed
  //   if (velocity > 0) {
  //     // Boost frequency by a factor of velocity
  //     frequencyMomentum.current += velocity * FREQ_FACTOR;
  //     // Increment speed by a fixed unit
  //     // speedMomentum.current += SPEED_INCREMENT;
  //   }

  //   // Apply linear decay (decrement over time until 0)
  //   // speedMomentum.current = Math.max(0, speedMomentum.current - DECAY);
  //   frequencyMomentum.current = Math.max(0, frequencyMomentum.current - DECAY);

  //   // Update targets based on base values + momentum
  //   // targetSpeedRef.current = 0.005 + speedMomentum.current;
  //   targetFrequencyRef.current = 0.1 + frequencyMomentum.current;

  //   // Smoothly interpolate current values towards targets (inertia effect)
  //   // speedRef.current += (targetSpeedRef.current - speedRef.current) * inertia;
  //   frequencyRef.current += (targetFrequencyRef.current - frequencyRef.current) * inertia;

  // }, []);
  // ✄ --------------------------------------------------------------

  const attack = useRef(false);
  const attackFactor = useRef(0.01);
  const decayFactor = useRef(0.008);

  // const sustainFactor = useRef(0.3);

  const attackRelease = useCallback(() => {
    let freq = frequencyRef.current;

    if (attack.current) {
      freq += attackFactor.current;
      freq = Math.min(freq, MAX_FREQUENCY);
    } else {
      freq -= decayFactor.current;
      freq = Math.max(freq, MIN_FREQUENCY);
    }

    frequencyRef.current = freq;
  }, []);

  // const attackRelease = useCallback(() => {
  //   let speed = speedRef.current;

  //   if (attack.current) {
  //     speed += attackFactor.current;
  //     speed = Math.min(speed, MAX_FREQUENCY);
  //   } else {
  //     speed -= decayFactor.current;
  //     speed = Math.max(speed, MIN_FREQUENCY);
  //   }

  //   speedRef.current = speed;
  // }, []);

  const drawWave = useCallback((t) => {
    let d = '';

    // Wave equation: y(x,t) = A * sin(2π * (x/λ) * f + φ - ωt) + y₀
    // Where:
    // - A = amplitude
    // - f = frequency (number of cycles)
    // - λ = wavelength (spatial period)
    // - φ = phase shift
    // - ω = angular velocity (temporal frequency)
    // - y₀ = vertical offset

    for (let i = 0; i <= segments; i++) {
      const x = (i / segments) * 100;
      const normalizedX = i / segments; // x position in [0, 1]
      
      // Spatial component: 2π * (x/λ) * f
      const spatialPhase = (2 * Math.PI * normalizedX / wavelength) * frequency;
      
      // Temporal component: -ωt (negative for rightward propagation)
      const temporalPhase = -angularVelocity * t;
      
      // Complete wave equation
      const y = verticalOffset + amplitude * Math.sin(spatialPhase + phaseShift + temporalPhase);
      
      d += i === 0 ? `M ${x},${y}` : ` L ${x},${y}`;
    }

    pathRef.current.setAttribute('d', d);
  }, [amplitude, frequency, wavelength, phaseShift, verticalOffset, angularVelocity, segments]);

  useAnimationFrame((time) => {
    if (!pathRef.current) return;
    attackRelease();
    timeRef.current = time;
    drawWave(time);
  }, [attackRelease, drawWave]);

  useEffect(() => {
    document.addEventListener("keydown", (e) => {
      if (e.key === 'w') {
        attack.current = true;
      }
    });
    document.addEventListener("keyup", (e) => {
      if (e.key === 'w') {
        attack.current = false;
      }
    });

    return () => {
      document.removeEventListener("keydown", () => { });
    };
  }, []);

  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 100 100`}
      style={{
        overflow: 'visible',
        width: '1rem',
        height: '1rem',
        ...style,
      }}
      preserveAspectRatio="none"
      stroke={stroke}
      fill="none"
    >
      <path
        ref={pathRef}
        d={`
          M 0,50
          Q 25,0 50,50
          Q 75,100 100,50
        `}
        vectorEffect="non-scaling-stroke"
        strokeWidth={strokeWidth}
      />
    </svg>
  );
}

